import * as angular from 'angular';

// language=HTML
const template = `
    <div class="modal-header">
        <h4 class="modal-title">Are you sure you want to leave?</h4>
    </div>
    <div class="modal-body" ng-show="$ctrl.isLoading">
        <h3 class="text-muted text-center"><i class="fa fa-spin fa-spinner"></i> Loading subscription status...</h3>
    </div>
    <ng-switch on="$ctrl.subscription.status" ng-hide="$ctrl.isLoading">
        <div class="modal-body" ng-switch-when="active">
            <p>
                You have an
                <span class="label label-success">active</span> subscription.
                If you decide to leave, your subscription will be cancelled,
                and you will be removed at <strong>{{$ctrl.subscription.next_payment_date | moment:'LL'}}</strong>.
            </p>

            <p>
                Until then, you can decide to continue your participation by clicking
                the button below.
            </p>

            <p><strong>Do you want to leave?</strong></p>
        </div>
        <div class="modal-body" ng-switch-when="pending-cancel">
            <p>
                Your subscription is <span class="label label-warning">pending cancellation</span>.
                You will be removed at <strong>{{ $ctrl.subscription.end_date | moment:'LL' }}</strong>.
            </p>

            <p>
                Until then, you can decide to continue your participation by clicking
                the button below.
            </p>

            <p><strong>Do you want to leave?</strong></p>
        </div>
        <div class="modal-body" ng-switch-default>
            <div class="alert alert-danger" ng-show="$ctrl.loadingError" ng-bind="$ctrl.loadingError"></div>
            <div class="alert alert-danger">
                Your subscription has unknown status.
            </div>
        </div>
    </ng-switch>

    <div class="modal-footer">
        <button class="btn btn-default" ng-click="$ctrl.actionLeave()" ng-disabled="!$ctrl.buttonsEnabled">
            I want to leave.
        </button>
        <button class="btn btn-success" ng-click="$ctrl.actionContinue()" ng-disabled="!$ctrl.buttonsEnabled">I want to
            stay!
        </button>
    </div>
`;

interface XyardsSubscription {
    id: number;
    status: string;
    next_payment_date?: string;
    cancelled_at?: string;
    // When subscription is cancelled, the next_payment_date becomes the end_date.
    end_date?: string;
}

export const ParticipantLeaveModalComponent: angular.IComponentOptions = {
    bindings: {
        close: '&',
        dismiss: '&',
        modalInstance: '=',
        resolve: '<',
    },
    template,
    controller: class ParticipantLeaveModalController implements angular.IComponentController {
        // 1. get subscription status when loading the modal
        // 2. display appropirate message depending on subscription status
        // 3. connect leave button to action -> make API call

        static $inject = ['$http', '$q'];
        private subscription?: XyardsSubscription;
        private isLoading: boolean = false;
        private loadingError?: string;

        private close!: () => void;
        private dismiss!: () => void;

        private resolve?: {
            entry: string;
            entryId: number;
        };

        constructor(private $http: angular.IHttpService, private $q: angular.IQService) {}

        get buttonsEnabled() {
            return this.subscription?.id && !this.isLoading;
        }

        _wrapInLoading(cb: () => angular.IPromise<XyardsSubscription>) {
            this.loadingError = undefined;
            this.isLoading = true;
            return cb()
                .then((sub) => (this.subscription = sub))
                .catch(
                    () =>
                        (this.loadingError =
                            'There was a problem while loading subscription. Please try again in a few minutes.')
                )
                .finally(() => (this.isLoading = false));
        }

        $onInit() {
            const { entry, entryId } = this.resolve!;

            this._wrapInLoading(() => {
                return this._fetchSubscription(entry, entryId);
            });
        }

        actionLeave() {
            if (this.subscription?.status == 'pending-cancel') return this.dismiss();

            this.subscription &&
                this._wrapInLoading(() => {
                    return this._cancelSubscription(this.subscription!.id);
                }).then(() => this.close());
        }

        actionContinue() {
            if (this.subscription?.status == 'active') return this.dismiss();

            this.subscription &&
                this._wrapInLoading(() => {
                    return this._continueSubscription(this.subscription!.id);
                }).then(() => this.close());
        }

        _cancelSubscription(id: number): angular.IPromise<XyardsSubscription> {
            return this.$http
                .put<{ data: XyardsSubscription }>(`/api/v1.0/my-subscriptions/${id}/cancel`, {})
                .then((response) => response.data.data);
        }

        _continueSubscription(id: number): angular.IPromise<XyardsSubscription> {
            return this.$http
                .put<{ data: XyardsSubscription }>(`/api/v1.0/my-subscriptions/${id}/continue`, {})
                .then((response) => response.data.data);
        }

        _fetchSubscription(entry: string, entryId: number): angular.IPromise<XyardsSubscription> {
            return this.$http
                .get<{ data: XyardsSubscription }>('/api/v1.0/my-subscriptions', {
                    params: { entry, entry_id: entryId },
                })
                .then((response) => response.data.data);
        }
    },
};
